import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionIcon from '@material-ui/icons/Description';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReplayIcon from '@material-ui/icons/Replay';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import VisibilityIcon from '@material-ui/icons/Visibility';
import WarningIcon from '@material-ui/icons/Warning';

import {
  baixarPacote,
  criarIngestao,
  podeBaixar,
  usePermissoesRelatoriosFiscais,
} from '../../../Resources/RelatoriosFiscaisExecucoes/acoes';
import DialogoGerar from '../../../Resources/RelatoriosFiscaisExecucoes/DialogoGerar';
import DialogoShowExecucao from '../../../Resources/RelatoriosFiscaisExecucoes/DialogoShow';
import { getListAPI } from '../../../services';
import { PRIMARY_COLOR } from '../../../utils';
import { GERAR_PACOTE_FISCAL_HABILITADO } from '../../../utils/constants';
import { PERMISSAO_TELA_RELATORIOS_FISCAIS_LISTAR } from '../../../utils/dadosTelas';
import { ProtegerPagina } from '../../VerificaPermissaoPage';
import useCalendarioFiscal from './useCalendarioFiscal';

const NOMES_MES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const CORES_DIA = {
  sem_execucao: '#bdbdbd',
  em_andamento: '#1976d2',
  sucesso: '#2e7d32',
  pendencias: '#ed6c02',
  erro: '#d32f2f',
};

const LIMITE_PENDENCIAS_NO_DIALOGO = 50;

const NOMES_TIPO_PENDENCIA = {
  sem_venda: 'Sem venda',
  sem_compra: 'Sem compra',
  erro_parse: 'Erro de parse',
  fora_periodo: 'Fora do período',
  erro_insert: 'Erro de insert',
};

const NOMES_FONTE = {
  nfce: 'NFC-e',
  nfe: 'NF-e',
  nfe_contingencia: 'NF-e contingência',
  compra: 'Compra',
};

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const STATUS_EXECUCAO = {
  pendente: { texto: 'Aguardando', cor: CORES_DIA.em_andamento },
  processando: { texto: 'Processando', cor: CORES_DIA.em_andamento },
  sucesso: { texto: 'Concluído', cor: CORES_DIA.sucesso },
  sucesso_com_pendencias: {
    texto: 'Concluído com pendências',
    cor: CORES_DIA.pendencias,
  },
  erro: { texto: 'Erro', cor: CORES_DIA.erro },
  cancelada: { texto: 'Cancelado', cor: CORES_DIA.sem_execucao },
};

// A ingestão é uma execução que cobre várias datas e todas herdam o mesmo
// status, então a cor do dia usa as pendências abertas do próprio dia.
function corDoDia(dia) {
  if (!dia.ingestao) return CORES_DIA.sem_execucao;
  if (['pendente', 'processando'].includes(dia.ingestao.status))
    return CORES_DIA.em_andamento;
  if (dia.ingestao.status === 'erro') return CORES_DIA.erro;
  if (['sucesso', 'sucesso_com_pendencias'].includes(dia.ingestao.status))
    return totalPendencias(dia) > 0 ? CORES_DIA.pendencias : CORES_DIA.sucesso;
  return CORES_DIA.sem_execucao;
}

// dia.data vem como AAAA-MM-DD; new Date() leria como UTC e mostraria o dia anterior.
function parseDataISO(dataISO) {
  const [ano, mes, dia] = dataISO.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

function totalNotas(dia) {
  return (
    (dia.notas.nfce || 0) +
    (dia.notas.nfe || 0) +
    (dia.notas.nfe_contingencia || 0) +
    (dia.notas.compra || 0)
  );
}

function totalPendencias(dia) {
  return Object.values(dia.pendencias_abertas || {}).reduce(
    (soma, valor) => soma + valor,
    0,
  );
}

const CORES_TIPO_PENDENCIA = {
  sem_venda: '#ed6c02',
  sem_compra: '#ed6c02',
  erro_parse: '#d32f2f',
  erro_insert: '#d32f2f',
  fora_periodo: '#7b1fa2',
};

function Indicador({ titulo, valor, cor = '#455a64', detalhe, icone }) {
  return (
    <Paper
      variant="outlined"
      style={{
        padding: '14px 16px',
        flex: '1 1 200px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        borderTop: `3px solid ${cor}`,
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: `${cor}1f`,
          color: cor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icone}
      </span>
      <Box>
        <Typography variant="caption" color="textSecondary" display="block">
          {titulo}
        </Typography>
        <Typography
          variant="h5"
          style={{ fontWeight: 700, color: cor, lineHeight: 1.2 }}
        >
          {valor}
        </Typography>
        {detalhe && (
          <Typography variant="caption" color="textSecondary" display="block">
            {detalhe}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function Secao({ titulo, children }) {
  return (
    <Box style={{ marginTop: 28 }}>
      <Typography
        variant="subtitle2"
        display="block"
        style={{
          fontWeight: 700,
          marginBottom: 10,
          paddingBottom: 6,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {titulo}
      </Typography>
      {children}
    </Box>
  );
}

function CardNotaTipo({ nome, total, maximo, cor }) {
  const pct = maximo > 0 ? Math.round((total / maximo) * 100) : 0;
  return (
    <Paper
      variant="outlined"
      style={{ padding: '10px 14px', flex: '1 1 150px' }}
    >
      <Typography variant="caption" color="textSecondary" display="block">
        {nome}
      </Typography>
      <Typography variant="h6" style={{ fontWeight: 700 }}>
        {total}
      </Typography>
      <div
        style={{
          height: 4,
          borderRadius: 2,
          backgroundColor: '#eeeeee',
          marginTop: 6,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 2,
            backgroundColor: cor,
          }}
        />
      </div>
    </Paper>
  );
}

function CopiarChave({ chave }) {
  const [copiado, setCopiado] = useState(false);

  function copiar() {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(chave).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    });
  }

  return (
    <Tooltip title={copiado ? 'Copiada!' : 'Copiar chave'}>
      <IconButton size="small" onClick={copiar}>
        <FileCopyIcon style={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function DialogoDia({ dia, onClose, onVerExecucao, podeGerar, onReingerir }) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [pendencias, setPendencias] = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  const dataDia = dia ? dia.data : null;
  const temPendencias = dia ? totalPendencias(dia) > 0 : false;

  useEffect(() => {
    setPendencias(null);
    setConfirmando(false);
    setErro(null);
    if (!dataDia || !temPendencias) return undefined;
    let cancelado = false;
    // Horário fixo ao meio-dia: a API usa startOf/endOf('day') e assim o
    // fuso do servidor não desloca a data.
    getListAPI(
      'relatorios_fiscais_pendencias',
      ['chave_acesso', 'asc'],
      [1, LIMITE_PENDENCIAS_NO_DIALOGO],
      {
        aberta: true,
        data_pasta: {
          start: `${dataDia}T12:00:00`,
          end: `${dataDia}T12:00:00`,
        },
      },
      [],
    )
      .then((res) => {
        if (!cancelado) setPendencias(res);
      })
      .catch(() => {
        if (!cancelado) setPendencias({ data: [], total: 0, falhou: true });
      });
    return () => {
      cancelado = true;
    };
  }, [dataDia, temPendencias]);

  if (!dia) return null;

  // dia.data vem como AAAA-MM-DD; reingerir só faz sentido até hoje.
  const agora = new Date();
  const hojeISO = `${agora.getFullYear()}-${String(
    agora.getMonth() + 1,
  ).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
  const podeReingerir = podeGerar && dia.data <= hojeISO;

  async function reingerir() {
    setEnviando(true);
    setErro(null);
    try {
      await onReingerir(dia.data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
      setConfirmando(false);
    }
  }

  const data = parseDataISO(dia.data);
  const dataFormatada = data.toLocaleDateString('pt-BR');
  const dataExtenso = data.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const status = dia.ingestao
    ? STATUS_EXECUCAO[dia.ingestao.status] || { texto: dia.ingestao.status }
    : { texto: 'Sem ingestão' };
  const pendenciasTotal = totalPendencias(dia);
  const notasVenda =
    (dia.notas.nfce || 0) +
    (dia.notas.nfe || 0) +
    (dia.notas.nfe_contingencia || 0);
  const diferencaVendas = dia.vendas_autorizadas_no_dia - notasVenda;

  const linhasNotas = [
    ['NFC-e', dia.notas.nfce, '#1976d2'],
    ['NF-e', dia.notas.nfe, '#00897b'],
    ['NF-e contingência', dia.notas.nfe_contingencia, '#f57c00'],
    ['Compras', dia.notas.compra, '#7b1fa2'],
  ];
  const maiorNota = Math.max(...linhasNotas.map(([, total]) => total || 0));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 8 }}
        >
          <Typography
            variant="h6"
            style={{ textTransform: 'capitalize', fontWeight: 600 }}
          >
            {dataExtenso}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Chip
              size="small"
              label={status.texto}
              style={{
                backgroundColor: corDoDia(dia),
                color: 'white',
                fontWeight: 600,
                marginRight: 8,
              }}
            />
            {podeReingerir && (
              <Tooltip title="Reingerir este dia">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={enviando}
                    onClick={() => setConfirmando(true)}
                  >
                    <ReplayIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Fechar">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
          <Indicador
            titulo="Notas ingeridas"
            valor={totalNotas(dia)}
            cor="#1976d2"
            icone={<DescriptionIcon />}
          />
          <Indicador
            titulo="Vendas autorizadas no dia"
            valor={dia.vendas_autorizadas_no_dia}
            cor="#455a64"
            icone={<ShoppingCartIcon />}
            detalhe={
              diferencaVendas !== 0
                ? `${Math.abs(diferencaVendas)} ${
                    diferencaVendas > 0 ? 'a mais' : 'a menos'
                  } que as notas de venda`
                : 'confere com as notas de venda'
            }
          />
          <Indicador
            titulo="Pendências abertas"
            valor={pendenciasTotal}
            cor={pendenciasTotal > 0 ? CORES_DIA.pendencias : CORES_DIA.sucesso}
            icone={pendenciasTotal > 0 ? <WarningIcon /> : <CheckCircleIcon />}
            detalhe={pendenciasTotal > 0 ? 'precisam de atenção' : 'tudo certo'}
          />
        </Box>

        <Secao titulo="Notas ingeridas por tipo">
          <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
            {linhasNotas.map(([nome, total, cor]) => (
              <CardNotaTipo
                key={nome}
                nome={nome}
                total={total || 0}
                maximo={maiorNota}
                cor={cor}
              />
            ))}
          </Box>
        </Secao>

        <Secao titulo="Ingestão">
          {dia.ingestao ? (
            <Paper
              variant="outlined"
              style={{
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                borderLeft: `4px solid ${corDoDia(dia)}`,
              }}
            >
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Execução #{dia.ingestao.execucao_id}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {dia.ingestao.finalizado_em
                    ? `Finalizada em ${new Date(
                        dia.ingestao.finalizado_em,
                      ).toLocaleString('pt-BR')}`
                    : 'Ainda não finalizada'}
                </Typography>
              </Box>
              <Tooltip title="Ver detalhes da execução">
                <IconButton
                  size="small"
                  onClick={() => onVerExecucao(dia.ingestao.execucao_id)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Nenhuma ingestão rodou para este dia.
            </Typography>
          )}
        </Secao>

        {temPendencias && (
          <Secao titulo="Pendências abertas">
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {Object.entries(dia.pendencias_abertas)
                .filter(([, total]) => total > 0)
                .map(([tipo, total]) => (
                  <Chip
                    key={tipo}
                    size="small"
                    label={`${NOMES_TIPO_PENDENCIA[tipo]}: ${total}`}
                    style={{
                      backgroundColor: `${CORES_TIPO_PENDENCIA[tipo]}1f`,
                      color: CORES_TIPO_PENDENCIA[tipo],
                      fontWeight: 600,
                    }}
                  />
                ))}
            </Box>
            {!pendencias && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                Carregando...
              </Typography>
            )}
            {pendencias && pendencias.falhou && (
              <Typography
                variant="body2"
                style={{ color: CORES_DIA.erro, marginTop: 8 }}
              >
                Não foi possível carregar as pendências.
              </Typography>
            )}
            {pendencias && !pendencias.falhou && (
              <>
                <Paper
                  variant="outlined"
                  style={{ marginTop: 12, maxHeight: 320, overflow: 'auto' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Chave de acesso', 'Tipo', 'Fonte', ''].map(
                          (titulo) => (
                            <TableCell
                              key={titulo || 'acoes'}
                              style={{
                                backgroundColor: '#f5f5f5',
                                fontWeight: 700,
                                fontSize: 12,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: '#616161',
                              }}
                            >
                              {titulo}
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendencias.data.map((pend, index) => (
                        <TableRow
                          key={pend.id}
                          hover
                          style={{
                            backgroundColor:
                              index % 2 ? '#fafafa' : 'transparent',
                          }}
                        >
                          <TableCell
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                          >
                            {pend.chave_acesso}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                NOMES_TIPO_PENDENCIA[pend.tipo] || pend.tipo
                              }
                              style={{
                                backgroundColor: `${
                                  CORES_TIPO_PENDENCIA[pend.tipo] || '#757575'
                                }1f`,
                                color:
                                  CORES_TIPO_PENDENCIA[pend.tipo] || '#757575',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {NOMES_FONTE[pend.fonte] || pend.fonte}
                          </TableCell>
                          <TableCell align="right" padding="none">
                            <CopiarChave chave={pend.chave_acesso} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
                {pendencias.total > pendencias.data.length && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 8 }}
                  >
                    Mostrando {pendencias.data.length} de {pendencias.total}{' '}
                    pendências.
                  </Typography>
                )}
              </>
            )}
          </Secao>
        )}

        {erro && (
          <Typography
            variant="body2"
            style={{ color: CORES_DIA.erro, marginTop: 16 }}
          >
            {erro}
          </Typography>
        )}
      </DialogContent>

      <Dialog
        open={confirmando}
        onClose={() => !enviando && setConfirmando(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reingerir {dataFormatada}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Será criada uma nova execução de ingestão para este dia. Os XMLs
            serão lidos novamente e as notas e pendências do dia poderão ser
            atualizadas. Confirme apenas se for realmente necessário.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={reingerir}
            disabled={enviando}
          >
            {enviando ? 'Enviando...' : 'Sim, reingerir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function CardDia({ dia, onClick }) {
  const pendencias = totalPendencias(dia);
  const semDados = !dia.ingestao && totalNotas(dia) === 0 && pendencias === 0;

  return (
    <Paper
      onClick={onClick}
      style={{
        padding: '8px 10px',
        cursor: 'pointer',
        minHeight: 78,
        boxSizing: 'border-box',
        borderLeft: `5px solid ${corDoDia(dia)}`,
        opacity: semDados ? 0.6 : 1,
      }}
    >
      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
        {parseDataISO(dia.data).getDate()}
      </Typography>
      <Typography variant="caption" display="block" color="textSecondary">
        {totalNotas(dia)} nota(s)
      </Typography>
      <Typography
        variant="caption"
        display="block"
        style={{
          color: pendencias > 0 ? CORES_DIA.pendencias : '#9e9e9e',
          fontWeight: pendencias > 0 ? 600 : 400,
        }}
      >
        {pendencias > 0 ? `${pendencias} pendência(s)` : 'sem pendências'}
      </Typography>
    </Paper>
  );
}

function Legenda() {
  const itens = [
    ['Sem pendências', CORES_DIA.sucesso],
    ['Com pendências', CORES_DIA.pendencias],
    ['Em andamento', CORES_DIA.em_andamento],
    ['Erro', CORES_DIA.erro],
    ['Sem ingestão', CORES_DIA.sem_execucao],
  ];
  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 12 }}>
      {itens.map(([texto, cor]) => (
        <Box key={texto} display="flex" alignItems="center" style={{ gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: cor,
            }}
          />
          <Typography variant="caption" color="textSecondary">
            {texto}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const NOMES_ORIGEM = {
  agendada: 'Agendada',
  manual: 'Manual',
  reprocessamento: 'Reprocessamento',
};

function formatarBytes(bytes) {
  if (!bytes) return '-';
  const mb = Number(bytes) / (1024 * 1024);
  return mb >= 1
    ? `${mb.toFixed(1)} MB`
    : `${Math.max(1, Math.round(Number(bytes) / 1024))} KB`;
}

function formatarDuracao(ms) {
  if (!ms) return '-';
  const segundos = Math.round(ms / 1000);
  if (segundos < 60) return `${segundos}s`;
  return `${Math.floor(segundos / 60)}min ${segundos % 60}s`;
}

function PainelPacote({
  ano,
  mes,
  pacoteAtual,
  versao,
  onVer,
  podeGerar,
  onGerar,
}) {
  const [pacotes, setPacotes] = useState(null);
  const [baixandoId, setBaixandoId] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setPacotes(null);
    getListAPI(
      'relatorios_fiscais_execucoes',
      ['id', 'desc'],
      [1, 20],
      { tipo: 'pacote_mensal', competencia_ano: ano, competencia_mes: mes },
      [],
    )
      .then((res) => {
        if (!cancelado) setPacotes(res.data);
      })
      .catch(() => {
        if (!cancelado) setPacotes([]);
      });
    return () => {
      cancelado = true;
    };
  }, [ano, mes, versao]);

  async function baixar(execucao) {
    setBaixandoId(execucao.id);
    setErro(null);
    try {
      await baixarPacote(execucao);
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixandoId(null);
    }
  }

  const idMaisRecente = pacoteAtual ? pacoteAtual.execucao_id : null;

  return (
    <Paper style={{ padding: 16, marginTop: 24 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 8, marginBottom: 8 }}
      >
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Pacotes fiscais de {NOMES_MES[mes - 1]} de {ano}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Cada geração cria um novo pacote; o mais recente é o vigente.
          </Typography>
        </Box>
        {podeGerar && GERAR_PACOTE_FISCAL_HABILITADO && (
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={onGerar}
          >
            Gerar pacote
          </Button>
        )}
      </Box>

      {pacoteAtual && pacoteAtual.dados_alterados_apos_geracao && (
        <Typography
          variant="body2"
          style={{ color: CORES_DIA.pendencias, marginBottom: 8 }}
        >
          Atenção: os dados mudaram depois que o pacote mais recente foi gerado.
          Gere um novo para refletir as alterações.
        </Typography>
      )}

      {!pacotes && (
        <Typography variant="body2" color="textSecondary">
          Carregando...
        </Typography>
      )}

      {pacotes && pacotes.length === 0 && (
        <Typography color="textSecondary" style={{ padding: '8px 0' }}>
          Nenhum pacote foi gerado para esta competência ainda.
        </Typography>
      )}

      {pacotes && pacotes.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pacote</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Gerado em</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Duração</TableCell>
              <TableCell>Tamanho</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pacotes.map((pacote) => {
              const status = STATUS_EXECUCAO[pacote.status] || {
                texto: pacote.status,
                cor: CORES_DIA.sem_execucao,
              };
              const expirado = !!pacote.artefatos_removidos_em;
              return (
                <TableRow key={pacote.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>#{pacote.id}</span>
                      {pacote.id === idMaisRecente && (
                        <Chip size="small" color="primary" label="Vigente" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={status.texto}
                      style={{
                        backgroundColor: status.cor,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {pacote.finalizado_em
                      ? new Date(pacote.finalizado_em).toLocaleString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {NOMES_ORIGEM[pacote.origem] || pacote.origem}
                  </TableCell>
                  <TableCell>{formatarDuracao(pacote.duracao_ms)}</TableCell>
                  <TableCell>
                    {formatarBytes(pacote.arquivo_zip_bytes)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => onVer(pacote.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {podeBaixar(pacote) ? (
                      <Tooltip title="Baixar pacote">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={baixandoId === pacote.id}
                            onClick={() => baixar(pacote)}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      expirado && (
                        <Tooltip title="Arquivo expirado">
                          <span>
                            <IconButton size="small" disabled>
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {erro && (
        <Typography
          variant="body2"
          style={{ color: CORES_DIA.erro, marginTop: 8 }}
        >
          {erro}
        </Typography>
      )}
    </Paper>
  );
}

function RelatoriosFiscaisAcompanhamento() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [dialogoGerarAberto, setDialogoGerarAberto] = useState(false);
  const refDialogoShow = useRef(null);
  const { podeGerar } = usePermissoesRelatoriosFiscais();

  const { dados, carregando, recarregar } = useCalendarioFiscal(ano, mes);

  function mudarMes(delta) {
    const data = new Date(ano, mes - 1 + delta, 1);
    setAno(data.getFullYear());
    setMes(data.getMonth() + 1);
  }

  function irParaHoje() {
    setAno(hoje.getFullYear());
    setMes(hoje.getMonth() + 1);
  }

  function handleVerExecucao(execucaoId) {
    setDiaSelecionado(null);
    if (refDialogoShow.current) refDialogoShow.current.handleOpen(execucaoId);
  }

  async function handleReingerir(dataISO) {
    const { execucao_id: execucaoId } = await criarIngestao({
      dataInicio: dataISO,
      dataFim: dataISO,
      forcado: false,
    });
    recarregar();
    handleVerExecucao(execucaoId);
  }

  const deslocamentoPrimeiroDia =
    dados && dados.dias.length ? parseDataISO(dados.dias[0].data).getDay() : 0;

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h6" style={{ marginBottom: 8 }}>
        Acompanhamento do mês
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        style={{ gap: 4, marginBottom: 12 }}
      >
        <IconButton size="small" onClick={() => mudarMes(-1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 600, minWidth: 170, textAlign: 'center' }}
        >
          {NOMES_MES[mes - 1]} de {ano}
        </Typography>
        <IconButton size="small" onClick={() => mudarMes(1)}>
          <ChevronRightIcon />
        </IconButton>
        <Button size="small" onClick={irParaHoje} style={{ marginLeft: 8 }}>
          Hoje
        </Button>
        {carregando && (
          <Typography variant="caption" color="textSecondary">
            Carregando...
          </Typography>
        )}
      </Box>

      <Legenda />

      {dados && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 8,
              marginTop: 24,
              marginBottom: 8,
              padding: '10px 0',
              borderRadius: 4,
              backgroundColor: PRIMARY_COLOR,
            }}
          >
            {DIAS_SEMANA.map((nome) => (
              <Typography
                key={nome}
                variant="caption"
                style={{
                  color: 'white',
                  fontWeight: 600,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {nome}
              </Typography>
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 8,
            }}
          >
            {Array.from({ length: deslocamentoPrimeiroDia }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`vazio-${i}`} />
            ))}
            {dados.dias.map((dia) => (
              <CardDia
                key={dia.data}
                dia={dia}
                onClick={() => setDiaSelecionado(dia)}
              />
            ))}
          </div>

          <PainelPacote
            ano={ano}
            mes={mes}
            pacoteAtual={dados.pacote}
            versao={dados}
            onVer={handleVerExecucao}
            podeGerar={podeGerar}
            onGerar={() => setDialogoGerarAberto(true)}
          />
        </>
      )}

      <DialogoDia
        dia={diaSelecionado}
        onClose={() => setDiaSelecionado(null)}
        onVerExecucao={handleVerExecucao}
        podeGerar={podeGerar}
        onReingerir={handleReingerir}
      />
      <DialogoGerar
        open={dialogoGerarAberto}
        onClose={() => setDialogoGerarAberto(false)}
        tipoInicial="pacote_mensal"
        anoInicial={ano}
        mesInicial={mes}
        onCriada={(execucaoId) => {
          recarregar();
          handleVerExecucao(execucaoId);
        }}
      />
      <DialogoShowExecucao ref={refDialogoShow} />
    </div>
  );
}

function Wrapper() {
  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_RELATORIOS_FISCAIS_LISTAR}>
      <RelatoriosFiscaisAcompanhamento />
    </ProtegerPagina>
  );
}

export default Wrapper;
